import { useMemo } from "react";
import type { BoundingBox } from "@design-lab/shared/types/base";
import type { ArtworkDimensions } from "../types";

export type Surface = {
  ppi: number;
  boundingBox: BoundingBox;
};

const minSizeInches = 0.1;

export function useCalculatedArtworkDimensions(
  image: HTMLImageElement | undefined,
  surface: Surface | undefined
): ArtworkDimensions {
  return useMemo(() => calculatedArtworkDimensions(image, surface), [image, surface]);
}

export function calculatedArtworkDimensions(
  image: HTMLImageElement | undefined,
  surface: Surface | undefined
): ArtworkDimensions {
  const naturalWidth = image?.naturalWidth;
  const naturalHeight = image?.naturalHeight;
  const ppi = surface?.ppi;
  const boundingBox = surface?.boundingBox;

  if (!naturalWidth || !naturalHeight || !ppi || !boundingBox) {
    return {
      minWidthInches: 0,
      minHeightInches: 0,
      maxWidthInches: 0,
      maxHeightInches: 0,
      originalWidthPx: naturalWidth || 0,
      originalHeightPx: naturalHeight || 0,
      maxAspectRatioByWidth: 0
    };
  }

  const imageAspect = naturalWidth / naturalHeight;

  const maxWidthInchesFromSurface = boundingBox.width / ppi;
  const maxHeightInchesFromSurface = boundingBox.height / ppi;

  let maxWidthInches: number;
  let maxHeightInches: number;

  if (maxWidthInchesFromSurface / imageAspect <= maxHeightInchesFromSurface) {
    maxWidthInches = maxWidthInchesFromSurface;
    maxHeightInches = maxWidthInchesFromSurface / imageAspect;
  } else {
    maxHeightInches = maxHeightInchesFromSurface;
    maxWidthInches = maxHeightInchesFromSurface * imageAspect;
  }

  const minHeightInches = Math.max(minSizeInches, minSizeInches / imageAspect);
  const minWidthInches = minHeightInches * imageAspect;

  const maxAspectRatioByWidth = maxWidthInches / maxHeightInches;

  return {
    minWidthInches: Number(minWidthInches.toFixed(2)),
    minHeightInches: Number(minHeightInches.toFixed(2)),
    maxWidthInches: Number(maxWidthInches.toFixed(2)),
    maxHeightInches: Number(maxHeightInches.toFixed(2)),
    originalWidthPx: naturalWidth,
    originalHeightPx: naturalHeight,
    maxAspectRatioByWidth: Number(maxAspectRatioByWidth.toFixed(2))
  };
}
